import { useEffect, useState } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import { api } from '../api/client';
import { getLang, setLang, t, type Lang } from '../i18n';
import { useLang } from '../lib/hooks';
import { navigate, routeHref, type RouteName } from '../router';

type HealthState = 'checking' | 'ok' | 'fail';

const HEALTH_POLL_MS = 60_000;

function HealthDot() {
  const [state, setState] = useState<HealthState>('checking');

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      try {
        await api.health();
        if (!cancelled) setState('ok');
      } catch {
        if (!cancelled) setState('fail');
      }
    };
    void check();
    const timer = setInterval(() => void check(), HEALTH_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  const label =
    state === 'ok' ? t('health.ok') : state === 'fail' ? t('health.fail') : t('health.checking');

  return (
    <span class={`health health--${state}`} title={label}>
      <span class="health__dot" aria-hidden="true" />
      <span class="health__label">{label}</span>
    </span>
  );
}

function LangToggle() {
  const active = useLang();
  const options: Lang[] = ['ru', 'en'];
  return (
    <div class="lang-toggle" role="group" aria-label={t('nav.lang')}>
      {options.map((lang) => (
        <button
          key={lang}
          type="button"
          class={`lang-toggle__btn${lang === active ? ' is-active' : ''}`}
          aria-pressed={lang === active}
          onClick={() => setLang(lang)}
        >
          {lang.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export function Layout({
  route,
  email,
  onLogout,
  children,
}: {
  route: RouteName;
  email: string | null;
  onLogout: () => void;
  children: ComponentChildren;
}) {
  // Subscribe so language switches re-render the whole shell.
  useLang();
  const links: Array<{ name: RouteName; label: string }> = [
    { name: 'vocab', label: t('nav.vocab') },
    { name: 'settings', label: t('nav.settings') },
    { name: 'anki', label: t('nav.anki') },
  ];

  return (
    <div class="shell">
      <a class="skip-link" href="#main">
        {t('nav.vocab')}
      </a>
      <header class="shell__header">
        <div class="shell__brand">
          <strong>{t('app.name')}</strong>
          <span class="muted shell__tagline">{t('app.tagline')}</span>
        </div>
        <nav class="shell__nav" aria-label={t('app.name')}>
          {links.map((link) => (
            <a
              key={link.name}
              href={routeHref(link.name as Exclude<RouteName, 'notFound'>)}
              class={route === link.name ? 'is-active' : ''}
              aria-current={route === link.name ? 'page' : undefined}
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div class="shell__aside">
          <HealthDot />
          <LangToggle />
          {email ? (
            <span class="shell__user" title={email}>
              {email}
            </span>
          ) : null}
          <button
            type="button"
            class="btn btn--ghost"
            onClick={() => {
              onLogout();
              navigate(routeHref('login'));
            }}
          >
            {t('nav.logout')}
          </button>
        </div>
      </header>
      <main id="main" class="shell__main">
        {children}
      </main>
      <footer class="shell__footer muted">
        <span>
          {t('health.base')}: <code>{window.location.origin}</code>
        </span>
        <span>· {getLang().toUpperCase()}</span>
      </footer>
    </div>
  );
}
